import React, { useRef, useEffect, useState } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { sanityClient, urlFor } from "../lib/sanityClient";
import { ease, fadeUp, maskRise, stagger, press } from "../lib/motion";
import "../styles/HeroSection.css";

const QUERY = `*[_type == "heroSection"][0]{
  tagline, taglineIcon, headlinePart1, headlinePart2,
  headlinePart3, subtext, ctaText, ctaUrl,
  heroImageDesktop, heroImageMobile
}`;



const HeroSection = ({ isSplash }) => {
  const [data, setData] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    sanityClient.fetch(QUERY).then(setData);
  }, []);

  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);
  const rotateX = useTransform(mouseY, [0, 1], [15, -15]);
  const rotateY = useTransform(mouseX, [0, 1], [-15, 15]);
  const smoothRotateX = useSpring(rotateX, { stiffness: 120, damping: 15 });
  const smoothRotateY = useSpring(rotateY, { stiffness: 120, damping: 15 });

  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    mouseX.set(0.5);
    mouseY.set(0.5);
  }, [mouseX, mouseY]);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024); // Desktop if width > 1024px
    };

    handleResize(); // Initial check
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleMouseMove = (e) => {
    const bounds = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - bounds.left) / bounds.width;
    const y = (e.clientY - bounds.top) / bounds.height;
    mouseX.set(x);
    mouseY.set(y);
  };

  if (isSplash) return null;

  return (
    <section className="hero" ref={ref}>
      {/* 🌟 Radial Glow Backgrounds */}
      <motion.div
        className="hero-glow-ellipse"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: [0, 0.75, 0.6, 0.75], scale: [0.8, 1, 1.06, 1] }}
        transition={{ duration: 3, ease, delay: 0.2, times: [0, 0.4, 0.7, 1] }}
      />
      <motion.div
        className="hero-glow-bottom-right"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: [0, 0.75, 0.6, 0.75], scale: [0.8, 1, 1.06, 1] }}
        transition={{ duration: 3, ease, delay: 0.4, times: [0, 0.4, 0.7, 1] }}
      />

      <div className="hero-overlay-wrapper">
        {data && (
        <motion.div
          className="hero-content"
          initial="hidden"
          animate="visible"
          variants={stagger(0.12, 0.2)}
        >
          <motion.p className="tagline" variants={fadeUp}>
            <img
              src={urlFor(data.taglineIcon).url()}
              alt="radio icon"
              className="tagline-icon"
            />
            {data.tagline}
          </motion.p>

          <h1 className="headline">
            {data.headlinePart1 && (
              <span className="reveal-mask">
                <motion.span style={{ display: "inline-block" }} variants={maskRise}>
                  {data.headlinePart1}
                </motion.span>
              </span>
            )}
            {data.headlinePart1 && <br />}
            {data.headlinePart2 && (
              <span className="reveal-mask">
                <motion.span
                  className="highlight"
                  style={{ display: "inline-block" }}
                  variants={maskRise}
                >
                  {data.headlinePart2}
                </motion.span>
              </span>
            )}
            {data.headlinePart3 && (
              <span className="reveal-mask">
                <motion.span style={{ display: "inline-block" }} variants={maskRise}>
                  {data.headlinePart3}
                </motion.span>
              </span>
            )}
          </h1>

          <motion.p className="subtext" variants={fadeUp} style={{ whiteSpace: "pre-line" }}>
            {data.subtext}
          </motion.p>

          <motion.div className="cta-buttons" variants={fadeUp}>
            <motion.button
              className="get-started"
              onClick={() => window.open(data.ctaUrl, "_blank")}
              {...press}
            >
              {data.ctaText}
              <img src="/ArrowIMG.svg" alt="arrow icon" className="arrow-icon" />
            </motion.button>
          </motion.div>
        </motion.div>
        )}

        <div
          className="hero-visual"
          onMouseMove={isDesktop ? handleMouseMove : undefined}
          onMouseLeave={() => {
            mouseX.set(0.5);
            mouseY.set(0.5);
          }}
          style={{
            perspective: 1000,
            transformStyle: "preserve-3d",
          }}
        >
          {data && (isDesktop ? (
            <motion.img
              src={urlFor(data.heroImageDesktop).url()}
              alt="Hero Visual"
              className="hero-image"
              style={{
                rotateX: smoothRotateX,
                rotateY: smoothRotateY,
              }}
              initial={{ opacity: 0, y: 40, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1, delay: 0.7, ease }}
            />
          ) : (
            <motion.img
              src={urlFor(data.heroImageMobile).url()}
              alt="Hero Visual"
              className="hero-image"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.6, ease }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
